/**
 * `AW48` · La **vista previa** del perfil de personalidad. `R1-F2 §4 Sección 1`.
 *
 * *«Formato: markdown, con vista previa al lado. **No un editor de texto
 * enriquecido.** La razón es concreta: un editor enriquecido guarda etiquetas de
 * formato que después habría que aplanar antes de mandárselas al modelo, o le
 * llegan como ruido. Markdown se ve estructurado en pantalla —títulos, listas,
 * negrita, párrafos—, se guarda como texto plano y los modelos lo leen
 * nativamente. El operador escribe algo organizado y el modelo recibe exactamente
 * lo que ve.»*
 *
 * ⛔⛔ **Y por eso esto NO usa `dangerouslySetInnerHTML` ni una librería de
 * markdown.** No es cautela genérica: lo que se está por dibujar es **texto que
 * el operador acaba de escribir en un campo público** —`§5.2`: durante R1
 * cualquiera que llegue a la consola puede editarlo— y `dangerouslySetInnerHTML`
 * lo convierte en HTML ejecutable en la misma pestaña que tiene la sesión.
 * Se construyen **elementos de React**, que React escapa por definición.
 *
 * ⭐ **Cubre exactamente lo que `R1-F2-17` verifica: títulos, listas y párrafos.**
 * No hay negrita, ni enlaces, ni tablas, y la ausencia es deliberada: cada regla
 * de más es una forma más de que la vista previa muestre algo distinto de lo que
 * el modelo va a recibir, y **lo que el modelo recibe es el texto crudo**. Lo que
 * no se reconoce se dibuja como párrafo, que es exactamente lo que el modelo va a
 * leer.
 */

import type { JSX } from "react"

type Bloque =
  | { readonly clase: "titulo"; readonly nivel: 1 | 2 | 3; readonly texto: string }
  | { readonly clase: "lista"; readonly items: readonly string[] }
  | { readonly clase: "parrafo"; readonly texto: string }

/**
 * Parte el texto en bloques. Exportada porque es la mitad que se puede ejercer
 * sin montar nada: `R1-F2-17` pide que la estructura quede intacta, y eso es una
 * afirmación sobre esta función.
 */
export function bloquesDelPerfil(fuente: string): readonly Bloque[] {
  const bloques: Bloque[] = []
  let parrafo: string[] = []
  let items: string[] = []

  const cerrarParrafo = (): void => {
    if (parrafo.length > 0) {
      bloques.push({ clase: "parrafo", texto: parrafo.join(" ") })
      parrafo = []
    }
  }
  const cerrarLista = (): void => {
    if (items.length > 0) {
      bloques.push({ clase: "lista", items })
      items = []
    }
  }

  for (const linea of fuente.split("\n")) {
    const encabezado = linea.match(/^(#{1,3})\s+(.*)$/)
    if (encabezado !== null) {
      cerrarParrafo()
      cerrarLista()
      bloques.push({
        clase: "titulo",
        nivel: encabezado[1]!.length as 1 | 2 | 3,
        texto: encabezado[2]!.trim(),
      })
      continue
    }

    const item = linea.match(/^\s*[-*]\s+(.*)$/)
    if (item !== null) {
      cerrarParrafo()
      items.push(item[1]!.trim())
      continue
    }

    if (linea.trim() === "") {
      cerrarParrafo()
      cerrarLista()
      continue
    }

    cerrarLista()
    parrafo.push(linea.trim())
  }

  cerrarParrafo()
  cerrarLista()
  return bloques
}

/** Dibuja los bloques como elementos de React. Nada de HTML crudo. */
export function VistaPreviaDelPerfil({ fuente }: { readonly fuente: string }): JSX.Element {
  const bloques = bloquesDelPerfil(fuente)

  if (bloques.length === 0) {
    return <p className="configuracion-chatbot__vacio">El perfil todavía no tiene texto.</p>
  }

  return (
    <div className="configuracion-chatbot__preview">
      {bloques.map((bloque, i) => {
        const clave = `${bloque.clase}-${String(i)}`
        if (bloque.clase === "titulo") {
          // Los títulos del perfil son contenido del operador, no la estructura
          // de la pantalla: van del `h3` para abajo para no competir con los
          // encabezados de las cuatro secciones en el árbol accesible.
          if (bloque.nivel === 1) return <h3 key={clave}>{bloque.texto}</h3>
          if (bloque.nivel === 2) return <h4 key={clave}>{bloque.texto}</h4>
          return <h5 key={clave}>{bloque.texto}</h5>
        }
        if (bloque.clase === "lista") {
          return (
            <ul key={clave}>
              {bloque.items.map((item, j) => (
                <li key={`${clave}-${String(j)}`}>{item}</li>
              ))}
            </ul>
          )
        }
        return <p key={clave}>{bloque.texto}</p>
      })}
    </div>
  )
}
